// Copyright © SRGTech (Software Resonance Group Inc.). All rights reserved.
// SPDX-License-Identifier: LicenseRef-SRGTech-Proprietary
// See LICENSE and docs/legal/PROPRIETARY_COPYRIGHT.md.

/**
 * Registered AI services: domain agents and MCP servers.
 *
 * `AIM-014` prohibits model-generated registry entries, so this screen is
 * read-only over the registry the platform owns. Enablement and health are
 * shown as reported; an unobserved health value renders as "Not reported"
 * rather than as healthy (`ADM-014`).
 */

import { capabilities, HealthBadge, queryKeys, StateBadge, type AiService } from '@eai/ui-shared';
import type { ReactNode } from 'react';

import { ResourceScreen, useControlPlaneQuery } from '../app-shell';

function serviceName(row: AiService): string {
  return row.agent?.name ?? row.mcp_server?.name ?? row.service_id;
}

function serviceVersion(row: AiService): string {
  return row.agent?.version ?? row.mcp_server?.version ?? 'Not reported';
}

/** AI service registry screen. */
export function AiServicesScreen(): ReactNode {
  const query = useControlPlaneQuery(
    (scope) => queryKeys.controlPlane.aiServices(scope),
    (client, signal) => client.listAiServices({ signal }),
  );

  return (
    <ResourceScreen<AiService>
      title="AI services"
      description="Agents and MCP servers registered for this platform. Registration, eligibility, and routing are owned by the platform; this screen reports them."
      requiredCapability={capabilities.aiServicesRead}
      area="AI services"
      query={query}
      rowKey={(row) => row.service_id}
      emptyTitle="No AI service is registered"
      emptyDescription="Register an agent or MCP server before it can be routed to."
      columns={[
        { key: 'name', header: 'Service', render: serviceName },
        {
          key: 'kind',
          header: 'Kind',
          render: (row) => (row.kind === 'agent' ? 'Agent' : 'MCP server'),
        },
        { key: 'version', header: 'Version', render: serviceVersion },
        {
          key: 'enabled',
          header: 'Routing',
          render: (row) => (
            <StateBadge
              label={row.enabled ? 'Enabled' : 'Disabled'}
              tone={row.enabled ? 'positive' : 'neutral'}
              subject={`${serviceName(row)} routing`}
            />
          ),
        },
        {
          key: 'health',
          header: 'Health',
          render: (row) => <HealthBadge health={row.health} subject={serviceName(row)} />,
        },
      ]}
    />
  );
}
